import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, Heading, Notification } from "@telia/teddy";
import { API_URL_SUPERADMIN_FEEDBACK } from "../../api";
import { type FeedbackEntry } from "./feedbackTypes";
import ReviewSummaryStats from "./ReviewSummaryStats";
import ReviewChart from "./ReviewChart";
import ReviewTrends from "./ReviewTrends";

const ReviewStatistics: React.FC = () => {
    const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [dealerCodeFilter, setDealerCodeFilter] = useState("");
    const [agentIdFilter, setAgentIdFilter] = useState("");
    const [agentNameFilter, setAgentNameFilter] = useState("");

    const filtersRef = useRef({ fromDate, toDate, dealerCodeFilter, agentIdFilter, agentNameFilter });
    filtersRef.current = { fromDate, toDate, dealerCodeFilter, agentIdFilter, agentNameFilter };

    const fetchFeedback = async () => {
        const {
            fromDate: from,
            toDate: to,
            dealerCodeFilter: dealerCode,
            agentIdFilter: agentId,
            agentNameFilter: agentName,
        } = filtersRef.current;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (from) params.set("from", from);
            if (to) params.set("to", to);
            if (dealerCode) params.set("dealerCode", dealerCode);
            if (agentId) params.set("agentId", agentId);
            if (agentName) params.set("agentName", agentName);

            const res = await fetch(`${API_URL_SUPERADMIN_FEEDBACK}?${params.toString()}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: FeedbackEntry[] = await res.json();
            setFeedback(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ukjent feil");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeedback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reviews = useMemo(() => feedback.filter((e) => e.feedbackType === "review"), [feedback]);
    const reviewsWithScore = useMemo(() => reviews.filter((e) => e.score !== null), [reviews]);
    const avgScore = useMemo(() => {
        if (reviewsWithScore.length === 0) return null;
        return Math.round((reviewsWithScore.reduce((sum, e) => sum + e.score!, 0) / reviewsWithScore.length) * 10) / 10;
    }, [reviewsWithScore]);

    return (
        <>
            <Box className="review-admin__filters">
                <Heading mb="100" as="h3" variant="title-100" className="review-admin__filters-heading">
                    Filter
                </Heading>
                <Flex direction="row" gap="400" wrap="wrap">
                    <Box>
                        <label htmlFor="review-filter-from" className="review-admin__filters-label">
                            Fra dato
                        </label>
                        <input
                            id="review-filter-from"
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="review-admin__filters-input"
                        />
                    </Box>
                    <Box>
                        <label htmlFor="review-filter-to" className="review-admin__filters-label">
                            Til dato
                        </label>
                        <input
                            id="review-filter-to"
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="review-admin__filters-input"
                        />
                    </Box>
                    <Box>
                        <label htmlFor="review-filter-dealer" className="review-admin__filters-label">
                            Forhandlerkode
                        </label>
                        <input
                            id="review-filter-dealer"
                            type="text"
                            value={dealerCodeFilter}
                            onChange={(e) => setDealerCodeFilter(e.target.value)}
                            placeholder="F.eks. 12345"
                            className="review-admin__filters-input"
                        />
                    </Box>
                    <Box>
                        <label htmlFor="review-filter-agent-id" className="review-admin__filters-label">
                            Agent-ID
                        </label>
                        <input
                            id="review-filter-agent-id"
                            type="text"
                            value={agentIdFilter}
                            onChange={(e) => setAgentIdFilter(e.target.value)}
                            placeholder="Agent-ID"
                            className="review-admin__filters-input"
                        />
                    </Box>
                    <Box>
                        <label htmlFor="review-filter-agent-name" className="review-admin__filters-label">
                            Agentnavn
                        </label>
                        <input
                            id="review-filter-agent-name"
                            type="text"
                            value={agentNameFilter}
                            onChange={(e) => setAgentNameFilter(e.target.value)}
                            placeholder="F.eks. Ola"
                            className="review-admin__filters-input"
                        />
                    </Box>
                    <Box className="review-admin__filters-submit">
                        <Button variant="secondary" size="sm" onClick={fetchFeedback} disabled={loading}>
                            {loading ? "Laster..." : "Oppdater"}
                        </Button>
                    </Box>
                </Flex>
            </Box>

            {error && (
                <Notification type="tile" variant="warning" className="review-admin__error">
                    <Notification.Icon />
                    <Notification.Text>Feil ved henting av vurderinger: {error}</Notification.Text>
                </Notification>
            )}

            <ReviewSummaryStats reviewCount={reviewsWithScore.length} avgScore={avgScore} />
            <ReviewChart feedback={reviews} />
            <ReviewTrends feedback={reviews} />
        </>
    );
};

export default ReviewStatistics;
